// ── Name / identity ────────────────────────────────────────────────────────

/** The single IndexedDB. Keep the legacy name so existing auth is not lost. */
export const SHARED_DB_NAME = "lokasi-intelligence";

/** Object store inside the DB (legacy name for redux-persist compatibility). */
export const STORE_NAME = "lokasi_intelligence_store";

/**
 * Marks a key as tab-scoped: `persist:map` -> `persist:tab:<id>:map`.
 * The `:` on both sides of the id prevents id "abc" matching "abc123".
 */
export const TAB_KEY_INFIX = ":tab:";

/** Live-tab list; sole source of truth for which tabs exist. */
export const TAB_REGISTRY_KEY = "__tabRegistry__";

/** Detects flag toggles (ON/OFF transition) across boots. */
export const STORAGE_MODE_KEY = "__storageMode__";

/** Single-writer migration lock. */
export const MIGRATION_LOCK_KEY = "__storageMigrationLock__";

/** Kept in the shared (non-namespaced) space so login is global. */
export const AUTH_PERSIST_KEY = "persist:auth";

/** React Query cache — shared across tabs. Matches the persister key. */
export const REACT_QUERY_PERSIST_KEY = "react-query";

/** Onboarding stays shared (global across tabs). */
export const ONBOARDING_PERSIST_KEY = "persist:onboarding";

/** Keys never namespaced (and never migrated). Internal `__*` keys are also treated as shared. */
export const SHARED_PRESERVED_KEYS: ReadonlySet<string> = new Set([
  AUTH_PERSIST_KEY,
  REACT_QUERY_PERSIST_KEY,
  ONBOARDING_PERSIST_KEY
]);

/** Name of the BroadcastChannel used for the PING/PONG presence protocol. */
export const PRESENCE_CHANNEL_NAME = "lokasi:tab-presence";

// ── Timing ─────────────────────────────────────────────────────────────────

/** Tab refreshes its "alive" mark this often. Must be << STALE_GRACE_MS. */
export const KEEP_ALIVE_INTERVAL_MS = 30_000; // 30s

/**
 * Idle threshold before a tab's keys can be swept. Kept comfortably above the
 * browser's ~5 min background-freeze so a frozen-but-alive tab (which stops
 * keep-alive and can't PONG) isn't wrongly wiped. Do not drop below ~10 min.
 */
export const STALE_GRACE_MS = 15 * 60_000; // 15 min

/** Send PING several times — a single PING can be missed if a tab is busy. */
export const PRESENCE_PING_ROUNDS = 3;

/** Delay between PING rounds. */
export const PRESENCE_PING_INTERVAL_MS = 400;

/** Window to collect PONGs (covers all rounds ~0/400/800 plus reply buffer). */
export const PRESENCE_TIMEOUT_MS = 1500;

/**
 * Duplicate-tab probe. Tighter than the sweep's presence check: it runs on the
 * boot path, and the tab it asks about was active a moment ago.
 */
export const CLAIM_PROBE_ROUNDS = 3;

/** Delay between claim-probe rounds. */
export const CLAIM_PROBE_INTERVAL_MS = 120;

/** Max wait for the claim probe (rounds at ~0/120/240 plus reply buffer). */
export const CLAIM_PROBE_TIMEOUT_MS = 500;

/** Periodic cleanup sweep interval. Paired with STALE_GRACE_MS: dead tabs clear in ~15-20 min. */
export const BACKGROUND_CLEANUP_INTERVAL_MS = 5 * 60_000; // 5 min

/** Min gap between sweeps triggered when a tab becomes visible. */
export const VISIBILITY_SWEEP_THROTTLE_MS = 60_000; // 60s

/** requestIdleCallback timeout for the first boot sweep. */
export const IDLE_CLEANUP_TIMEOUT_MS = 3000;

/** Migration lock lifetime — long enough for a normal migration, short enough to recover if the holder dies. */
export const MIGRATION_LOCK_TTL_MS = 30_000; // 30s
